import { useMemo, useCallback, useState } from "react";
import useLocalStorage from "react-use-localstorage";
import { GameDataItem } from "./GameData";
import { Skills, EMPTY_SKILLS, xpToLevel, apply, add } from "./Skills";
import { uuidv7 } from "uuidv7";
import { useUserRepository } from "../repositories/UserRepository";
import { useDebouncedCallback } from "use-debounce";
import dayjs from "dayjs";
import { LOCAL_STORAGE_SUFFIX, UserPreferenceData } from "../PlayTracker";

export interface UseUserDataResult {
    userData: UserData;
    userDataAllTime: UserData;
    updateUserData: (newUserData: Partial<UserData>) => void;
    addGame: (gameToAdd: GameDataItem, playedOnOverride?: Date) => void;
    removeGame: (gameIndexToRemove?: number) => void;
    updateProfile: (fields: UserUpdateProfileProps) => void;
    loginProfile: (fields: UserLoginProfileProps) => void;
    loadingGame?: GamePlayedItem;
}

export type UserUpdateProfileProps = Partial<Pick<UserData, 'name' | 'icon'>>;
export type UserLoginProfileProps = Pick<UserData, 'userId' | 'userSecret'>;

export type UseUserDataProps = { preferences?: UserPreferenceData };

export const useUserData = ({ preferences }: UseUserDataProps = {}): UseUserDataResult => {
    const { updateUser: updateUserImmediately, createPlay, deletePlay, listUsers } = useUserRepository();
    const updateUser = useDebouncedCallback(updateUserImmediately, 3000);
    const [storedUserData, setStoredUserData] = useLocalStorage(`play-tracker-user-data${LOCAL_STORAGE_SUFFIX}`, JSON.stringify(DEFAULT_USER));
    const userData: UserData = useMemo(() => !!storedUserData ? JSON.parse(storedUserData) : { gamesPlayed: [] }, [storedUserData]);
    const setUserData = useCallback((newUserData: UserData) => {
        setStoredUserData(JSON.stringify(newUserData));
    }, [setStoredUserData]);
    const [loadingGame, setLoadingGame] = useState<GamePlayedItem>();

    const updateUserData = useCallback((newUserData: Partial<UserData>) => {
        setUserData({ ...userData, ...newUserData });
    }, [setUserData, userData]);

    const addGame = useCallback((gameToAdd: GameDataItem, playedOnOverride?: Date) => {
        const playedOn = new Date(playedOnOverride ?? dayjs().toDate());
        setLoadingGame({ ...gameToAdd, playedOn, userId: '', gameId: '', playId: '' });
        void (async () => {
            if (!userData.gamesPlayed.length) {
                await updateUser({
                    userId: userData.userId,
                    userSecret: userData.userSecret,
                    name: userData.name,
                    icon: userData.icon,
                });
            }
            const play = await createPlay({
                userId: userData.userId,
                userSecret: userData.userSecret,
                gameId: gameToAdd.id,
                playedOn: playedOn.toISOString(),
            });
            if (play) {
                const updatedGamesPlayed = [{ ...gameToAdd, ...play, playedOn }, ...(userData.gamesPlayed ?? [])];
                const updatedUserData = { ...userData, gamesPlayed: updatedGamesPlayed };
                setUserData(updatedUserData);
                setLoadingGame(undefined);
            }
        })();
    }, [userData, setUserData, createPlay, updateUser]);

    const removeGame = useCallback((gameIndexToRemove?: number) => {
        if (userData.gamesPlayed && gameIndexToRemove !== undefined) {
            const gameToRemove = userData.gamesPlayed[gameIndexToRemove];
            const updatedGamesPlayed = [...userData.gamesPlayed.slice(0, gameIndexToRemove), ...userData.gamesPlayed.slice(gameIndexToRemove + 1)];
            const updatedUserData = { ...userData, gamesPlayed: updatedGamesPlayed };
            setUserData(updatedUserData);
            void deletePlay({
                userId: userData.userId,
                userSecret: userData.userSecret,
                playId: gameToRemove.playId,
            });
        }
    }, [userData, setUserData, deletePlay]);

    const updateProfile = useCallback((fields: UserUpdateProfileProps) => {
        const newUserData = { ...userData, icon: fields.icon ?? userData.icon, name: fields.name ?? userData.name };
        setUserData(newUserData);
        void updateUser({
            userId: newUserData.userId,
            userSecret: newUserData.userSecret,
            name: newUserData.name,
            icon: newUserData.icon,
        });
    }, [userData, setUserData, updateUser]);

    const loginProfile = useCallback((fields: UserLoginProfileProps) => {
        void (async () => {
            const users = await listUsers({ userId: fields.userId, userSecret: fields.userSecret });

            if (users && users.length) {
                const localUserData = users.find((user) => user.userId === fields.userId);

                if (localUserData) {
                    const newUserData: UserData = { ...localUserData, userId: fields.userId, userSecret: fields.userSecret, gamesPlayed: [], status: 'pending' };
                    setUserData(newUserData);
                }
            } else {
                alert("Unknown user. Please check your user key and try again.");
            }
        })();
    }, [listUsers, setUserData]);

    var filteredUserData = useMemo(() => {
        if (preferences?.yearFilter) {
            const filteredGamesPlayed = userData.gamesPlayed.filter((game) => dayjs(game.playedOn).year().toString() === preferences.yearFilter);
            return { ...userData, gamesPlayed: filteredGamesPlayed };
        }
        return userData;
    }, [userData, preferences?.yearFilter]);

    return {
        userData: filteredUserData,
        userDataAllTime: userData,
        updateUserData,
        addGame,
        removeGame,
        updateProfile,
        loginProfile,
        loadingGame,
    }
}

export interface UserData {
    userId: string;
    userSecret: string;
    name: string;
    icon: string;
    gamesPlayed: readonly GamePlayedItem[];
    status: 'pending' | 'active';
}

export interface GamePlayedItem extends GameDataItem {
    userId: string;
    gameId: string;
    playId: string;
    playedOn: Date;
}

export function xps(userData?: UserData): Skills {
    let xp = { ...EMPTY_SKILLS };

    if (userData) {
        xp = xpsFromPlays(userData.gamesPlayed);
    }

    return xp;
}

export function xpsFromPlays(plays: readonly GamePlayedItem[]): Skills {
        let xp = { ...EMPTY_SKILLS };

    if (plays) {
        for (const game of plays) {
            xp = add(xp, game.xp);
        }
    }

    return xp;
}

export function levelsFromXp(xp: Skills): Skills {
    return apply(xp, xpToLevel);
}

export function levels(userData?: UserData): Skills {
    return levelsFromXp(xps(userData));
}

const USER_NAME_PREFIXES = ['Alert', 'Blue', 'Calm', 'Dark', 'Erratic', 'Fair', 'Glib', 'High', 'Impish', 'Jaded', 'Kind', 'Lush', 'Meek', 'New', 'Odd', 'Puny', 'Quick', 'Red', 'Sad', 'Tall', 'Ultra', 'Vast', 'Wise', 'Xtra', 'Yummy', 'Zany'] as const;
const USER_NAME_SUFFIXES = ['Ape', 'Bat', 'Cat', 'Dog', 'Elk', 'Frog', 'Goat', 'Hippo', 'Ibex', 'Jackal', 'Kiwi', 'Lemur', 'Mule', 'Newt', 'Ox', 'Pig', 'Quokka', 'Rat', 'Seal', 'Toad', 'Urial', 'Viper', 'Worm', 'Xerus', 'Yak', 'Zebra'] as const;
export const USER_ICONS = ['🐵', '🦇', '🐈', '🐶', '🫎', '🐸', '🐐', '🦛', '🪲' , '🎃', '🥝', '🍋', '🫏', '🦎', '🐂', '🐷', '🐭', '🐀', '🦭', '🍄', '🦑', '🐍', '🪱', '💀', '🤖', '🦓']

function randomUser(): UserData {
    function getRandomInt(min: number, max: number): number {
        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    const prefixIndex = getRandomInt(0, USER_NAME_PREFIXES.length - 1);
    const suffixIndex = getRandomInt(0, USER_NAME_SUFFIXES.length - 1);
    const iconIndex = getRandomInt(0, USER_ICONS.length - 1);

    return {
        userId: uuidv7(),
        userSecret: uuidv7(),
        icon: USER_ICONS[iconIndex],
        name: '',
        gamesPlayed: [],
        status: "active",
    };
}

const DEFAULT_USER: UserData = randomUser();
